import FormGroup from 'live/components/forms/ui/form-group'
import _ from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

export type ParametersTableDataType = {
    [hearderName: string]: {
        index: number
        customHeaderClass?: string
        hideHeader?: boolean
        inputRender?: (
            value,
            onChange: (newValue: any) => void,
            lineIndex?: number,
            className?: string
        ) => React.ReactElement
        viewRender?: (value, lineIndex?: number, className?: string) => React.ReactElement
        label?: string | React.ReactNode
        styles?: React.CSSProperties
        customClassnames?: string[]
        edit?: boolean
        min?: number
        type?:
            | 'button'
            | 'checkbox'
            | 'color'
            | 'date'
            | 'datetime-local'
            | 'email'
            | 'file'
            | 'hidden'
            | 'image'
            | 'month'
            | 'number'
            | 'password'
            | 'radio'
            | 'range'
            | 'reset'
            | 'search'
            | 'submit'
            | 'tel'
            | 'text'
            | 'time'
            | 'url'
            | 'week'
        data: (number | string | React.ReactNode)[]
    }
}

export type ParameterTablePropertiesType =
    | 'index'
    | 'customHeaderClass'
    | 'hideHeader'
    | 'label'
    | 'styles'
    | 'edit'
    | 'type'

type ParametersTablePropType = {
    tableData: ParametersTableDataType
    onChange: React.Dispatch<React.SetStateAction<ParametersTableDataType>>
    hideData?: boolean
    styledCss?: string
    readOnly?: boolean
    height?: number
    setReadOnly?: React.Dispatch<React.SetStateAction<boolean>>
    className?: string
}
export function ParametersTable({
    tableData,
    onChange,
    styledCss,
    hideData,
    readOnly,
    setReadOnly,
    height,
    className
}: ParametersTablePropType) {
    const [editingCell, setEditingCell] = useState<[string, number]>([null, null])
    const headersLabels = Object.keys(tableData).sort((a, b) => (tableData[a].index > tableData[b].index ? 1 : -1))

    const [isComponentVisible, setIsComponentVisible] = useState(true)
    const ref = useRef(null)

    const maxColumnName = Object.keys(tableData).sort((a, b) =>
        tableData[b].data.length > tableData[a].data.length ? 1 : -1
    )[0]

    const handleClick = (editing: boolean, colName: string, rowIndex: number, evt: React.MouseEvent<MouseEvent>) => {
        setReadOnly?.(false)
        setIsComponentVisible(true)
        if (!editing) {
            setEditingCell([colName, rowIndex])
            ;(evt.target as HTMLElement).querySelector('div>input')?.focus()
        }
    }

    const handleChange = (colName: string, rowIndex: number, value: string) => {
        const newTableData = _.cloneDeep(tableData)
        newTableData[colName].data[rowIndex] = value
        onChange(newTableData)
    }

    const handleUserKeyPress = evt => {
        // Get TabEvent to select the active cell
        const activeElement = evt.target
        if (evt.key && activeElement.className.includes('table-input-content')) {
            const [activeHeaderName, activeRowIndex] = activeElement.id.split('$')
            setEditingCell(() => [activeHeaderName, parseInt(activeRowIndex)])
        }
    }

    const handleClickOutside = event => {
        if (ref.current && !ref.current.contains(event.target)) {
            setIsComponentVisible(false)
        }
    }

    useEffect(() => {
        window.addEventListener('keyup', handleUserKeyPress)
        window.addEventListener('click', handleClickOutside, true)
        return () => {
            window.removeEventListener('keyup', handleUserKeyPress)
            window.removeEventListener('click', handleClickOutside, true)
        }
    }, [handleUserKeyPress])
    return (
        <>
            <StyledSheet className={className} ref={ref} height={height} dataTable={tableData} styledCss={styledCss}>
                <thead>
                    <tr>
                        {headersLabels.map(headerName => {
                            const titleName = !tableData[headerName].hideHeader
                                ? tableData[headerName].label || headerName
                                : ''
                            return (
                                <th
                                    key={tableData[headerName].index}
                                    id={`${headerName}$${tableData[headerName].index}-th`}
                                    className={`table-col-header table-col-header-${headerName
                                        ?.toLowerCase()
                                        .replaceAll(' ', '_')} ${titleName} ${tableData[headerName].customHeaderClass ||
                                        ''}`}
                                >
                                    {titleName}
                                </th>
                            )
                        })}
                    </tr>
                </thead>
                <tbody>
                    {!hideData &&
                        tableData[maxColumnName]?.data.map((maxColumnHeaderName, i) => {
                            return (
                                <tr key={`${maxColumnName}$${i}-tr`} id={`${maxColumnName}$${i}-tr`}>
                                    {headersLabels.map(headerName => {
                                        const lineContent = tableData[headerName]
                                        const editing = !!(
                                            lineContent?.edit &&
                                            editingCell[0] === headerName &&
                                            editingCell[1] === i
                                        )
                                        const valueChanged = false

                                        return (
                                            <td
                                                key={`${headerName}$${i}-td`}
                                                id={`${headerName}$${i}-td`}
                                                onClick={evt => handleClick(editing, headerName, i, evt)}
                                                className={`${
                                                    !readOnly && isComponentVisible && editing ? 'editing' : ''
                                                } table-col table-col-${headerName
                                                    .toLowerCase()
                                                    .replaceAll(' ', '_')} ${lineContent.customClassnames?.[i] || ''}`}
                                            >
                                                {!readOnly && lineContent?.edit
                                                    ? lineContent?.inputRender?.(
                                                          lineContent.data[i],
                                                          e => handleChange(headerName, i, e),
                                                          i,
                                                          `table-input-content`
                                                      ) || (
                                                          <FormGroup
                                                              className={`table-input-content`}
                                                              id={`${headerName}$${i}`}
                                                              type={lineContent.type}
                                                              onChange={e => {
                                                                  const value = e.target.value
                                                                  const v = isNaN(lineContent.min)
                                                                      ? value
                                                                      : lineContent.min > Number(value)
                                                                      ? `${lineContent.min}`
                                                                          : value
                                                                  handleChange(headerName, i, v)
                                                              }}
                                                              min={lineContent.min}
                                                              value={lineContent.data[i].toString()}
                                                          />
                                                      )
                                                    : lineContent?.viewRender?.(
                                                          lineContent.data[i],
                                                          i,
                                                          `table-input-content`
                                                      ) || lineContent.data[i]}
                                            </td>
                                        )
                                    })}
                                </tr>
                            )
                        })}
                </tbody>
            </StyledSheet>
        </>
    )
}

export const changeParameterProperty = (
    tableData: ParametersTableDataType,
    colName: string,
    propName: ParameterTablePropertiesType,
    valueData: unknown
) => {
    tableData[colName][propName] = valueData
    return tableData
}

export const addParameterData = (tableData: ParametersTableDataType, colName: string, valueData: unknown) => {
    tableData[colName].data.push(valueData)
    return tableData
}

export const addLineParameterData = (tableData: ParametersTableDataType, valueData: { [colName: string]: unknown }) => {
    _.forEach(tableData, (col, colName) => {
        tableData[colName].data.push(valueData[colName])
    })
    return tableData
}

export const removeParameterData = (tableData: ParametersTableDataType, colName: string, index: number) => {
    delete tableData[colName].data[index]
    return tableData
}

export const cleanParameterData = (tableData: ParametersTableDataType, colName: string, index: number) => {
    tableData[colName].data[index] = ''
    return tableData
}

export const changeParameterData = (
    tableData: ParametersTableDataType,
    colName: string,
    index: number,
    value: unknown
) => {
    tableData[colName].data[index] = value
    return tableData
}

export const removeLineParameterData = (tableData: ParametersTableDataType, index: number) => {
    _.forEach(tableData, (col, colName) => {
        delete tableData[colName].data[index]
    })
    return tableData
}

export const getParameterData = (tableData: ParametersTableDataType, colName: string, index: number): unknown => {
    return tableData[colName].data[index]
}

const StyledSheet = styled.table<{ height: number; dataTable: ParametersTableDataType; styledCss: string }>`
    ${({ height }) => height && `height: ${height}px;`}
    font-size: 13px;
    //table-layout: fixed;

    ${({ styledCss }) => styledCss}
`
